import React, { useState, useEffect } from 'react';
import { FaEye, FaTrash } from 'react-icons/fa';
import TiendaDetailModal from './TiendaDetailModal';
import { get, del } from '../../../helpers/apiHelper';

const TruncatedText = ({ text, maxLength = 50 }) => {
    if (!text) return null;
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;

    return (
        <div className="relative group">
            <div className="text-sm text-gray-500 truncate max-w-xs">
                {displayText}
            </div>
            {shouldTruncate && (
                <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-sm rounded-md p-2 -mt-1 max-w-xs break-words whitespace-normal">
                    {text}
                </div>
            )}
        </div>
    );
};

const LocationInfo = ({ tienda }) => {
    const fullAddress = `${tienda.via} ${tienda.calle}, ${tienda.numero}`;
    const fullLocation = `${tienda.poblacion}, ${tienda.region}`;

    return (
        <div className="relative group">
            <div>
                <div className="text-sm text-gray-900 truncate max-w-xs">
                    {fullAddress}
                </div>
                <div className="text-sm text-gray-500 truncate max-w-xs">
                    {fullLocation}
                </div>
            </div>
            <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-sm rounded-md p-2 -mt-1 max-w-xs break-words whitespace-normal">
                <p className="font-medium">{fullAddress}</p>
                <p className="text-gray-300">{fullLocation}</p>
            </div>
        </div>
    );
};

const TiendasList = () => {
    const [tiendas, setTiendas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedTienda, setSelectedTienda] = useState(null);

    useEffect(() => {
        fetchTiendas();
    }, []);

    const fetchTiendas = async () => {
        try {
            setLoading(true);
            const data = await get('tiendas/admin');
            setTiendas(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (tiendaId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta tienda? Esta acción no se puede deshacer.')) return;

        try {
            await del(`tiendas/${tiendaId}`);
            await fetchTiendas(); // Refresh the list
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewDetails = (tienda) => {
        setSelectedTienda(tienda);
        setShowDetailModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailModal(false);
        setSelectedTienda(null);
    };

    if (loading) return <div className="text-center p-4">Cargando...</div>;
    if (error) return <div className="text-red-500 p-4">{error}</div>;

    return (
        <div className="container mx-auto p-4">
            <div className="mb-6">
                <h2 className="text-2xl font-bold">Gestión de Tiendas</h2>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full table-auto">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clientes</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propietario</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {tiendas.map((tienda) => (
                            <tr key={tienda._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{tienda.nombre}</div>
                                    <TruncatedText text={tienda.descripcion} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <LocationInfo tienda={tienda} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{tienda.telefono}</div>
                                    <div className="text-sm text-gray-500">{tienda.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {tienda.clientes?.length || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{tienda.usuario?.nombre}</div>
                                    <div className="text-sm text-gray-500">{tienda.usuario?.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={() => handleViewDetails(tienda)}
                                            className="text-blue-600 hover:text-blue-900"
                                            title="Ver detalles"
                                        >
                                            <FaEye className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(tienda._id)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Eliminar tienda"
                                        >
                                            <FaTrash className="h-5 w-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showDetailModal && (
                <TiendaDetailModal
                    tienda={selectedTienda}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default TiendasList; 